import {useCallback, useRef, useState} from 'react'
import {twMerge} from 'tailwind-merge'
import {ReactComponent as ArrowTopIcon} from './arrow_top.svg'
import {ReactComponent as CautionIcon} from './caution.svg'
import {ReactComponent as InfoWarningIcon} from './info_warning.svg';
import {useParams} from "react-router-dom";
import {
  useSelfCustomAvatarHistoryQuery
} from "../../../../../../services/selfCustomAvatar/useSelfCustomAvatarHistoryQuery";
import {useDebounce} from "react-use";
import {useMutation} from "@tanstack/react-query";
import {selfCustomAvatar} from "../../../../../../services/selfCustomAvatar";
import {CircularProgress} from "@mui/material";
import {useTranslation} from "react-i18next";

function Step2CreateForm({formData, setFormData, prev, close}) {
  const {workspaceId} = useParams()
  const fileInputRef = useRef(null)
  const dropRef = useRef(null)
  const {i18n} = useTranslation()
  const isKorean = i18n.language.startsWith('ko')
  const guideUrl = isKorean
    ? 'https://aivatar.gitbook.io/user-guide/ko/product-lineup/self-custom-avatar/self-filming-guide'
    : 'https://aivatar.gitbook.io/user-guide/ko/product-lineup/self-custom-avatar/self-filming-guide'

  const [validation, setValidation] = useState({
    character: null,
    length: null,
    unique: null,
    fileExist: false,
    fileSize: null,
    duration: null,
  })
  const [isGuideOpen, setIsGuideOpen] = useState(true)
  const [isDragActive, setIsDragActive] = useState(false)

  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false)
  const [isBackConfirmModalOpen, setIsBackConfirmModalOpen] = useState(false)

  const {data: history = []} = useSelfCustomAvatarHistoryQuery(Number(workspaceId))
  const existingNames = history
    .filter((item) => item.status !== 'FAIL')
    .map((item) => item.name)

  const isVideoFile = (file) => {
    if (!file) return false
    const validExtensions = ['.mp4', '.webm', '.mov']
    const fileName = file.name.toLowerCase()
    const hasValidExtension = validExtensions.some(ext => fileName.endsWith(ext))
    const isVideoType = file.type.startsWith('video/')
    return isVideoType && hasValidExtension
  }

  const handleFile = useCallback((file) => {
    if (!isVideoFile(file)) return

    const video = document.createElement('video')
    video.preload = 'metadata'
    video.onloadedmetadata = () => {
      setFormData(prev => ({
        ...prev,
        file,
        videoMeta: {duration: video.duration}
      }))
      URL.revokeObjectURL(video.src)
    }
    video.src = URL.createObjectURL(file)
  }, [setFormData])

  const handleDrop = (e) => {
    e.preventDefault()
    setIsDragActive(false)
    const file = e.dataTransfer?.files?.[0]
    if (file) handleFile(file)
  }

  const handleDrag = (e) => {
    e.preventDefault()
    e.stopPropagation()
    if (['dragenter', 'dragover'].includes(e.type)) {
      setIsDragActive(true)
    } else if (e.type === 'dragleave') {
      setIsDragActive(false)
    }
  }

  const handleNameChange = (e) => {
    setFormData((prev) => ({...prev, name: e.target.value}))
  }

  const toggleGuide = () => setIsGuideOpen((prev) => !prev)

  useDebounce(() => {
    const {name, file, videoMeta = {}} = formData

    setValidation({
      character: /^[ㄱ-ㅎ가-힣a-zA-Z0-9]+$/.test(name),
      length: name.length > 0 && name.length <= 15,
      unique: name.length > 0 && !existingNames.includes(name),
      fileExist: !!file,
      fileSize: file?.size != null ? file.size <= 200 * 1024 * 1024 : null,
      duration: typeof videoMeta.duration === 'number' ? videoMeta.duration <= 60 : null,
    })
  }, 300, [formData.name, formData.file, formData.videoMeta, existingNames])

  const allValid = Object.values(validation).every((v) => v === true)

  const {mutate, isPending} = useMutation({
    mutationFn: selfCustomAvatar,
    onSuccess: () => {
      setIsConfirmModalOpen(false)
      setFormData({name: '', file: null})
      prev()
    },
    onError: (e) => {
      console.error('아바타 생성 실패: ', e)
    }
  })

  const handleConfirmCreate = () => {
    if (!formData.file || !formData.name) return
    mutate({
      workspaceId: Number(workspaceId),
      name: formData.name,
      userFile: formData.file,
    })
  }

  const isInvalidName =
    formData.name.length > 0 &&
    (
      validation.character === false ||
      validation.length === false ||
      validation.unique === false
    )

  return (
    <div
      className="w-full sm:max-w-[780px] bg-white rounded-[9.73px] overflow-hidden flex flex-col max-h-[calc(100vh-80px)]">
      {/* 콘텐츠 영역: 스크롤 가능 */}
      <div className="flex-1 overflow-y-auto px-4 sm:px-5 py-6 flex flex-col items-center gap-6">
        {/* Header */}
        <div className="w-full flex justify-between items-center">
          <h2 className="text-headline02 text-black">셀프 커스텀 아바타</h2>
          <button onClick={close} className="w-6 h-6" aria-label="닫기">✕</button>
        </div>

        {/* 이름 입력 */}
        <div className="w-full border border-gs01-100 rounded-lg px-5 py-4 flex flex-col gap-3">
          <div className="text-subtitle01 text-gs01-800 text-base font-bold">1. 아바타 이름 설정</div>
          <input
            type="text"
            value={formData.name}
            onChange={handleNameChange}
            placeholder="아바타 이름을 입력해 주세요."
            className={twMerge(
              "h-10 w-full px-3 bg-white border rounded text-body01 text-sm text-gs01-900 placeholder:text-gs01-300 outline-none",
              isInvalidName
                ? "border-red-500 outline outline-1 outline-offset-[-0.5px] outline-red-500"
                : "border-gs01-200"
            )}
          />
          <div className="bg-gs01-20 p-3 rounded-lg flex flex-col gap-2">
            <ValidationRow isValid={validation.character} text="글자는 한글, 로마 알파벳 및 숫자로 제한됩니다."/>
            <ValidationRow isValid={validation.length} text="15자 이내로 작성할 수 있습니다."/>
            <ValidationRow isValid={validation.unique} text="기존 셀프 커스텀 아바타 이름과 중복되지 않아야 합니다."/>
          </div>
        </div>

        {/* 파일 업로드 외곽 실선 */}
        <div
          ref={dropRef}
          className={twMerge(
            "w-full outline outline-1 outline-gs01-100 rounded-lg px-5 py-3.5 flex flex-col gap-[5px]",
            isDragActive && "ring-2 ring-blue-300 bg-blue-50"
          )}
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
        >
          {/* 상단 타이틀 */}
          <div className="text-subtitle01 text-gs01-800 text-base font-bold">2. 파일 업로드</div>

          {/* 점선 내부 레이어 (색 고정) */}
          <div
            className="w-full mt-3 px-2.5 py-7 rounded-lg outline outline-1 outline-dashed outline-gs01-200 flex flex-col justify-center items-center gap-2.5"
          >
            {/* 파일 미선택 상태 */}
            {!formData.file && (
              <>
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className="h-8 px-4 py-3 bg-bcblue-500 rounded-full inline-flex justify-center items-center text-body01 text-white text-sm font-bold"
                >
                  파일 선택하기
                </button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".mp4,.webm,.mov"
                  hidden
                  onChange={(e) => {
                    const file = e.target.files?.[0]
                    if (file) handleFile(file)
                  }}
                />
                <p className="text-body01 text-xs text-gs01-800 font-medium">또는 파일을 여기에 끌어다 놓으세요.</p>
              </>
            )}

            {/* 파일 선택된 경우: 점선 안에 실선 박스 추가 */}
            {formData.file && (
              <div className="w-full flex flex-col items-start gap-1 max-w-[600px]">
                {/* 실선 박스: 파일 정보 */}
                <div
                  className={twMerge(
                    "w-full px-5 py-4 bg-white rounded-lg flex justify-between items-center border",
                    (validation.fileSize === false || validation.duration === false) ? "border-red-500" : "border-gs01-200"
                  )}
                >
                  <div className="flex px-3 items-center gap-[12px] overflow-hidden">
                    {/* 파일 아이콘 */}
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <g clipPath="url(#clip0_1655_12954)">
                        <path
                          d="M15 1H4C2.9 1 2 1.9 2 3V16C2 16.55 2.45 17 3 17C3.55 17 4 16.55 4 16V4C4 3.45 4.45 3 5 3H15C15.55 3 16 2.55 16 2C16 1.45 15.55 1 15 1ZM15.59 5.59L20.42 10.42C20.79 10.79 21 11.3 21 11.83V21C21 22.1 20.1 23 19 23H7.99C6.89 23 6 22.1 6 21L6.01 7C6.01 5.9 6.9 5 8 5H14.17C14.7 5 15.21 5.21 15.59 5.59ZM15 12H19.5L14 6.5V11C14 11.55 14.45 12 15 12Z"
                          fill="#576076"
                        />
                      </g>
                      <defs>
                        <clipPath id="clip0_1655_12954">
                          <rect width="24" height="24" fill="white"/>
                        </clipPath>
                      </defs>
                    </svg>

                    <span className="text-body01 text-sm text-gs01-900 truncate">{formData.file.name}</span>
                  </div>

                  {/* 닫기 버튼 */}
                  <button
                    onClick={() => setFormData((prev) => ({...prev, file: null, videoMeta: {}}))}
                    aria-label="파일 삭제"
                    type="button"
                    className="shrink-0"
                  >
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none"
                         xmlns="http://www.w3.org/2000/svg">
                      <path
                        d="M11.9998 1.78C6.34979 1.78 1.77979 6.35 1.77979 12C1.77979 17.65 6.35979 22.22 11.9998 22.22C17.6398 22.22 22.2198 17.64 22.2198 12C22.2198 6.36 17.6498 1.78 11.9998 1.78ZM17.1698 16.16C17.4498 16.44 17.4498 16.89 17.1698 17.17C16.8898 17.45 16.4398 17.45 16.1598 17.17L11.9998 13.01L7.83979 17.17C7.55979 17.45 7.10979 17.45 6.82979 17.17C6.54979 16.89 6.54979 16.44 6.82979 16.16L10.9898 12L6.82979 7.84C6.54979 7.56 6.54979 7.11 6.82979 6.83C7.10979 6.55 7.55979 6.55 7.83979 6.83L11.9998 10.99L16.1598 6.83C16.4398 6.55 16.8898 6.55 17.1698 6.83C17.4498 7.11 17.4498 7.56 17.1698 7.84L13.0098 12L17.1698 16.16Z"
                        fill="#576076"
                      />
                    </svg>
                  </button>
                </div>

                {/* 에러 메시지 (같은 정렬 내 포함됨) */}
                {(validation.fileSize === false || validation.duration === false) && (
                  <div className="flex flex-col gap-1 pt-1">
                    {validation.fileSize === false && (
                      <div className="flex items-center gap-[5px] text-body01 text-xs text-red-500">
                        <CautionIcon className="w-5 h-5"/>
                        <span>파일 크기가 200MB를 초과합니다.</span>
                      </div>
                    )}
                    {validation.duration === false && (
                      <div className="flex items-center gap-[5px] text-body01 text-xs text-red-500">
                        <CautionIcon className="w-5 h-5"/>
                        <span>동영상 길이가 1분을 초과합니다.</span>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* 하단 유효성 안내 메시지 */}
          <div className="bg-gs01-20 p-3 rounded-lg flex flex-col gap-2 mt-2">
            <ValidationRow isValid={validation.fileSize} text="파일 크기는 200MB 이하여야 합니다."/>
            <ValidationRow isValid={validation.duration} text="동영상의 길이는 1분 이하로 지켜 주세요."/>
          </div>
        </div>

        {/* 영상 업로드 및 사용 동의 */}
        <div className="w-full flex flex-col gap-1">
          <div className="h-10 pl-9 pr-5 py-[5px] bg-orange-200 rounded flex items-center">
            <span className="text-subtitle02 text-gs01-800 text-sm font-bold">
              🚨 영상 업로드 및 사용 동의
            </span>
          </div>

          <div className="px-11 py-3.5 bg-gs01-50 rounded flex flex-col gap-3.5">
            <div className="flex flex-col gap-1">
              <div className="text-body01 text-gs01-700 text-sm leading-tight">
                <span className="font-bold">얼굴 및 초상권 관련 동의 확인</span>
                <span className="font-normal">
                  : 회원은 업로드하는 영상에 등장하는 인물이 (1) 본인이거나, (2) 본인이 아닐 경우 해당 인물로부터 영상 사용에 대한 명시적 동의(동의서, 계약서, 위임장 등 법적 효력이 있는 서면 증빙)를 얻었음을 확인합니다. 무단으로 타인의 얼굴이나 초상을 사용하여 발생하는 모든 법적 책임은 회원에게 있습니다.
                </span>
              </div>
              <div className="text-body01 text-gs01-700 text-sm leading-tight">
                <span className="font-bold">서비스 이용 수칙 및 법적 책임:</span>
                <span className="font-normal">
                  {' '}
                  회원은 AiVATAR 플랫폼 및 AiVATAR 기술이 적용된 모든 서비스(API 포함)에서 생성된 콘텐츠를 불법적이거나 유해한 목적으로 사용하지 않을 것을 확인합니다. 회원은 타인의 권리를 침해하는 영상 파일의 사용을 금지하며, 저작권이 있는 영상을 무단으로 사용할 경우 법적 책임이 발생할 수 있음을 확인합니다. 또한 회원은 (주)에이아이파크의 서비스 약관, 금지 콘텐츠 및 사용 정책, 개인정보 보호정책을 준수할 것을 확인합니다.
                </span>
              </div>
              <div className="text-body01 text-gs01-700 text-sm font-normal leading-tight">
                &#39;생성하기&#39; 버튼을 클릭하시면 위 내용을 모두 확인하고 이에 동의하신 것으로 간주됩니다.
              </div>
            </div>
          </div>
        </div>

        {/* 주의사항 토글 */}
        <div className="w-full flex flex-col gap-1">
          <div
            role="button"
            tabIndex={0}
            onClick={toggleGuide}
            onKeyDown={(e) => ['Enter', ' '].includes(e.key) && toggleGuide()}
            className="flex justify-between items-center bg-blue-50 px-4 py-[6px] rounded cursor-pointer"
          >
            <span className="text-subtitle02 text-gs01-800 text-sm font-bold">💡 촬영 및 업로드 시 주의사항 안내</span>
            <span className={twMerge(!isGuideOpen ? 'rotate-180' : '', 'transition-transform')}>
              <ArrowTopIcon/>
            </span>
          </div>

          {isGuideOpen && (
            <div className="self-stretch px-11 py-3.5 bg-gs01-50 rounded flex flex-col gap-3">
              <div className="flex flex-col gap-[3px]">
                <GuideBullet text="영상에는 1명의 사람만 등장해야 합니다."/>
                <GuideBullet text="손이나 소품으로 얼굴을 가리지 않도록 주의해 주세요."/>
                <GuideBullet text="중간에 자르거나 편집하지 말고 연속된 촬영을 유지하세요."/>
                <GuideBullet text="촬영 시작과 중단 시 카메라 흔들림에 주의하세요."/>
                <GuideBullet text="지원되는 코덱은 H.264, H.265, MPEG-4 Part 2, AV1 입니다."/>
              </div>
              <div className="self-stretch text-sm font-medium text-body01 text-gs01-800 px-1">
                <span>더 나은 결과를 위해 </span>
                <a
                  href={guideUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-950 underline"
                >
                  상세 가이드↗
                </a>
                <span>를 확인해 보세요.</span>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* CTA 버튼: 하단 고정 */}
      <div
        className="w-full px-4 sm:px-5 py-3 pb-[26px] border-t border-gs01-100 bg-white flex justify-between items-center">
        <button
          className="h-8 px-4 bg-white text-bcblue-500 border border-bcblue-500 text-body01 text-sm font-bold rounded-lg"
          onClick={() => setIsBackConfirmModalOpen(true)}
        >
          이전으로
        </button>
        <button
          className={twMerge(
            "h-8 px-4 text-body01 !text-white text-sm font-bold rounded-lg",
            allValid ? "bg-bcblue-500" : "bg-gs01-100"
          )}
          disabled={!allValid}
          onClick={() => setIsConfirmModalOpen(true)}
        >
          생성하기
        </button>
      </div>

      {/* 확인 모달 */}
      {isConfirmModalOpen && (
        <div className="fixed inset-0 bg-black/50 flex justify-center items-center z-50">
          <div
            className="p-10 bg-white rounded-lg shadow-[12px_14px_27.4px_0px_rgba(87,96,118,0.30)] inline-flex flex-col justify-center items-center gap-7">
            <div className="flex flex-col justify-start items-center gap-7">
              <div className="flex flex-col justify-start items-center gap-3">
                <div className="w-9 h-9 relative overflow-hidden">
                  <InfoWarningIcon className="w-9 h-9 absolute text-[#C5D0D9]"/>
                </div>
                <div className="flex flex-col justify-start items-center gap-2">
                  <div className="text-subtitle01 text-center text-grayscale-01-800 text-xl font-bold">
                    셀프 커스텀 아바타 생성하기
                  </div>
                  <div className="flex flex-col items-center text-body01 text-grayscale-01-600 text-sm font-normal">
                    <span>확인을 눌러서 아바타 생성을 요청합니다.</span>
                    <span>아바타 생성에는 수 분이 소요됩니다.</span>
                  </div>
                </div>
              </div>
              <div className="inline-flex justify-center items-center gap-4">
                <button
                  onClick={() => setIsConfirmModalOpen(false)}
                  className="w-32 h-10 px-5 py-3 bg-white rounded-lg outline outline-1 outline-offset-[-0.5px] outline-bcblue-500 flex justify-center items-center gap-[5px]"
                >
                  <div className="text-body01 text-bcblue-500 text-sm font-bold">취소</div>
                </button>
                <button
                  type="button"
                  onClick={handleConfirmCreate}
                  disabled={isPending}
                  className={twMerge(
                    "w-32 h-10 px-5 py-3 rounded-lg flex justify-center items-center gap-[5px]",
                    isPending
                      ? "bg-gs01-100 text-white cursor-not-allowed pointer-events-none"
                      : "bg-bcblue-500 text-white hover:bg-bcblue-600 cursor-pointer",
                    "transition"
                  )}
                >
                  <span className="text-body01 text-sm font-bold flex items-center gap-1">
                    확인
                    {isPending && <CircularProgress size={18} color="inherit"/>}
                  </span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {isBackConfirmModalOpen && (
        <div className="fixed inset-0 bg-black/50 flex justify-center items-center z-50">
          <div
            className="p-10 bg-white rounded-lg shadow-[12px_14px_27.4px_0px_rgba(87,96,118,0.30)] inline-flex flex-col justify-center items-center gap-7">
            <div className="flex flex-col justify-start items-center gap-7">
              <div className="flex flex-col justify-start items-center gap-3">
                <div className="w-9 h-9 relative overflow-hidden">
                  <InfoWarningIcon className="w-9 h-9 absolute text-[#C5D0D9]"/>
                </div>
                <div className="flex flex-col justify-start items-center gap-2">
                  <div className="text-subtitle01 text-center text-grayscale-01-800 text-xl font-bold">
                    이전 화면으로 돌아가기
                  </div>
                  <div className="flex flex-col items-center text-grayscale-01-600 text-body01 text-sm font-normal">
                    <span>확인을 눌러서 대시보드 화면으로 돌아갑니다.</span>
                  </div>
                </div>
              </div>
              <div className="inline-flex justify-center items-center gap-4">
                <button
                  onClick={() => setIsBackConfirmModalOpen(false)}
                  className="w-32 h-10 px-5 py-3 bg-white rounded-lg outline outline-1 outline-offset-[-0.5px] outline-bcblue-500 flex justify-center items-center gap-[5px]"
                >
                  <div className="text-body01 text-bcblue-500 text-sm font-bold">취소</div>
                </button>
                <button
                  onClick={() => {
                    setIsBackConfirmModalOpen(false)
                    setFormData({name: '', file: null})
                    prev()
                  }}
                  className="w-32 h-10 px-5 py-3 rounded-lg bg-bcblue-500 hover:bg-bcblue-600 text-white flex justify-center items-center gap-[5px] transition"
                >
                  <div className="text-body01 text-sm font-bold">확인</div>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

// 유효성 안내
function ValidationRow({isValid, text}) {
  return (
    <div className="flex items-center gap-1">
      <ValidationIcon className={twMerge(isValid && '[&_path]:fill-green-400')}/>
      <span className={twMerge(isValid ? 'text-green-400' : 'text-gs01-800', 'text-body01 text-xs')}>
        {text}
      </span>
    </div>
  )
}

// 점 아이콘 안내 항목
function GuideBullet({text}) {
  return (
    <div className="self-stretch flex items-start gap-[6px]">
      <div className="w-1 h-1 rounded-full bg-gs01-500 mt-[7px]"/>
      <div className="text-body01 text-gs01-700 text-sm font-normal">
        {text}
      </div>
    </div>
  )
}

// 회색 체크 아이콘
function ValidationIcon({className}) {
  return (
    <svg className={className} width="16" height="16" viewBox="0 0 16 16" fill="none">
      <path
        d="M8.00008 1.16666C4.22675 1.16666 1.16675 4.22666 1.16675 7.99999C1.16675 11.7733 4.22675 14.8333 8.00008 14.8333C11.7734 14.8333 14.8334 11.7733 14.8334 7.99999C14.8334 4.22666 11.7734 1.16666 8.00008 1.16666ZM12.2934 6.02666L7.49341 11.2067C7.41341 11.2933 7.30008 11.34 7.18675 11.3467H7.18008C7.06675 11.3467 6.96008 11.3 6.87341 11.22L3.72008 8.06666C3.55341 7.89999 3.55341 7.62666 3.72008 7.45999C3.88675 7.29332 4.16008 7.29332 4.32675 7.45999L7.16008 10.2933L11.6601 5.43999C11.8201 5.26666 12.0934 5.25332 12.2667 5.41999C12.4401 5.57999 12.4534 5.85332 12.2867 6.02666H12.2934Z"
        fill="#C5D0D9"
      />
    </svg>
  )
}

export default Step2CreateForm
